import React, { useCallback } from 'react';

import IconCalendar from '../../atoms/icons/icon-calendar';
import Card from '../../molecules/card/card';
import DataCenterSkeleton from '../../molecules/data-center-skeleton/data-center-skeleton';
import { CourtesyMessage } from '../../organisms';
import ComposableList, { IComposableListAccessibleItem } from '../../organisms/composable-list/composable-list';
import { IComponent } from '../../../typings/generic-component';
import { makeClassNames } from '../../../utils/styles';
import { objectToDataAttributes } from '../../../utils/tracking-data';
import t from '../../../labels/labelling-utils';
import './card-motorsports-calendar.scss';
import Text from '../../atoms/text/text';
import { dateFormat, upperDate } from '../../../utils/times';
import FlagAndName from '../../atoms/flag-and-name/flag-and-name';

export interface ICardMotorsportsCalendar extends IComponent {
  competitionId?: string;
  customCompetitionName?: string;
  calendarLink?: string;
  gpList: IGp[];
  isLoading?: boolean;
  trackingData?: {};
  cardCtaTrackingData?: {};
}

export interface IGp extends IComposableListAccessibleItem {
  customGpName: string;
  flagUrl: string;
  startDateTime: string;
  endDateTime: string;
  isExclusive: boolean;
}

interface IMotorsportsCalendarClasses {
  card: string;
  gpWrapper: string;
  gpDate: string;
  gpRow: string;
}

const isValidGpList = (gpList: IGp[], isLoading: boolean) => isLoading || Boolean(gpList && gpList.length);
const backgroundIcon = () => <IconCalendar size="medium" color="light" />;

const classNames = (): IMotorsportsCalendarClasses =>
  makeClassNames({
    card: 'card-motorsports-calendar',
    gpWrapper: 'card-motorsports-calendar__gp-wrapper',
    gpDate: 'card-motorsports-calendar__gp-date',
    gpRow: 'card-motorsports-calendar__gp-row',
  }) as IMotorsportsCalendarClasses;

const CardMotorsportsCalendar: React.FC<ICardMotorsportsCalendar> = ({
  gpList,
  isLoading = false,
  calendarLink,
  customCompetitionName,
  trackingData = {},
  cardCtaTrackingData = {},
}) => {
  const cn = classNames();

  const getDate = useCallback((startDateTime: string, endDateTime: string): string => {
    const start: Date = new Date(startDateTime);
    const end: Date = new Date(endDateTime);
    return `${`${upperDate(
      dateFormat({ date: startDateTime, format: start.getMonth() === end.getMonth() ? 'D' : 'D MMMM' })
    )}`}-${upperDate(
      dateFormat({
        date: endDateTime,
        format: 'D MMMM YYYY',
      })
    )}`;
  }, []);

  return (
    <aside role="document" {...objectToDataAttributes(trackingData)}>
      {isLoading ? (
        <DataCenterSkeleton size="small" rowCount={5} />
      ) : (
        <Card
          cta={isValidGpList(gpList, isLoading) ? t('card.view.all') : t('view.all.results')}
          ctaLink={calendarLink}
          ctaMobile
          ctaMobileWhite
          title={`${t('calendar.cta')} Formula 1`}
          size="small"
          className={cn.card}
          aria-label="calendario"
          aria-description={`${t('calendar.cta')} ${customCompetitionName}`}
          trackingData={cardCtaTrackingData}
        >
          {isValidGpList(gpList, isLoading) ? (
            gpList.map((gp: IGp, index: number) => {
              return (
                <ComposableList<IGp>
                  items={[gp]}
                  key={`GP-${index}`}
                  fields={[
                    {
                      prop: 'title',
                      render: (data) => (
                        <FlagAndName flagUrl={data.flagUrl} name={data.customGpName} fontSize="16--14" />
                      ),
                    },
                    {
                      prop: 'isExclusive',
                      render: (data) => (
                        <Text fontSize="14" color="light">
                          {data.isExclusive ? t('motorsports.live.exclusive') : t('motorsports.live')}
                        </Text>
                      ),
                    },
                  ]}
                  headers={{ title: getDate(gp.startDateTime, gp.endDateTime), isExclusive: '' }}
                  inverted
                  aria-label="calendario"
                  aria-description={`${t('calendar.cta')} ${customCompetitionName}`}
                />
              );
            })
          ) : (
            <CourtesyMessage
              backgroundIcon={backgroundIcon()}
              text={t('card.view.unavailable')}
              date={`${t('card.view.unavailable.cta')}`}
              fontSize="20"
              colorSchema="dark"
              cardLayout
              trackingData={cardCtaTrackingData}
            />
          )}
        </Card>
      )}
    </aside>
  );
};

export default CardMotorsportsCalendar;
